import React, { useCallback, useMemo } from 'react';
import { Typography } from 'antd';

import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import ToggleRow from '../common/ToggleRow';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { buildFilterFields, tagsFilterField } from '../../utils/kqlBuilder';
import { normalizeId } from '../../utils/ids';
import { useCollectionScope } from '../../contexts/CollectionScopeContext';
import { lookupFilterOf, lookupSourceCollectionId, lookupTargetCardIds } from '../../utils/lookupFilter';

/**
 * "Offer only items where…" — a card lookup's picker filter
 * (docs/plans/data-types-second-pass.md §3.7, PR 6; the owner's ruling D10).
 *
 * Under the lookup card select, because the filter is ABOUT that card: its
 * conditions name the TARGET card's elements (its own, inherited ones and its
 * child cards'), never this card's — keepr-api refuses a name the target does
 * not have. The builder is the list's own (`FilterBuilder`), the summary the
 * list's own chips (`FilterChips`), so a filter reads the same here as on any
 * list.
 *
 * `strict` is a ToggleRow under the chips, offered only once there is a
 * filter (the API refuses strict without one) and never on a calculated
 * lookup (its value is computed, not chosen). Clearing the filter clears
 * strict with it, so a save never carries one without the other.
 *
 * `me` is held by the builder (`disallowMe`): the filter is read where no one
 * is signed in — an import, a rule — and the API refuses it.
 *
 * TAGS (keepr-api docs/SCHEMA.md, "The picker filter"): the filter is text
 * the collection runs, so its `tags` field is the COLLECTION mode, as a
 * rule's condition is (ConditionBuilder) — the tags of the collection whose
 * items the picker lists and its parent's, picked by id and checked when
 * typed; a private tag is never offered, and one typed is flagged. The API
 * stores a name as its tag's id, so a rename never breaks the filter. A card
 * with no collection (a global one) gets no tags field: no name resolves
 * there.
 *
 * The builder keeps the card, set and facet KEYS the text is written with
 * (`keepKeys`): the API reads them itself — in the target's home collection
 * when the card is saved, or, on a card with no collection (a global one,
 * ruling E9), in each collection that uses it — so this page, which knows
 * the target's family in every collection it can read, never writes one
 * collection's card id in their place (the F3 fix review's N2).
 */

const { Text } = Typography;

export const LOOKUP_FILTER_LABEL = 'Offer only items where…';
export const LOOKUP_FILTER_ME_NOTE = 'This filter can’t use me: imports and rules have nobody signed in.';
export const STRICT_LABEL = 'Strict';
export const STRICT_ON = 'Only these items can be chosen — on the form, in imports and by rules. Items already chosen keep their value.';
export const STRICT_OFF = 'These items are offered first, and Show all chooses from every item.';
export const STRICT_DRIVEN = 'A calculated lookup can’t be strict — its value is computed, not chosen.';

const labelOf = card => card?.label?.plural || card?.name || 'items';
const NO_TAGS = Object.freeze([]);

export default function LookupFilterOptions({ element, index, onChange, cardsById = null, getElementsForCard = null, disabled = false }) {
	const targetId = normalizeId(element?.options?.lookupCardId);
	const filter = lookupFilterOf(element?.options);
	const strict = element?.options?.strict === true;
	const driven = !!element?.options?.drivenFrom;
	const { collectionId } = useCollectionScope();
	const targetDef = targetId ? cardsById?.[targetId] || null : null;
	const sourceCollectionId = lookupSourceCollectionId(targetDef, collectionId) || null;

	// The target's vocabulary: the target and the cards under it, each read
	// through the resolver that folds in ancestors and element sets.
	const tagsOn = Boolean(targetId && sourceCollectionId);
	const { tags: collectionTags, loaded: tagsLoaded } = useCollectionTags(sourceCollectionId, { enabled: tagsOn });
	const myTags = useOptionalTags()?.tags || NO_TAGS;
	const tagsField = useMemo(() => (tagsOn ? tagsFilterField({
		tags: collectionTags,
		privateTags: myTags,
		mode: 'collection',
		loaded: tagsLoaded,
	}) : null), [tagsOn, collectionTags, myTags, tagsLoaded]);
	const fields = useMemo(() => {
		if (!targetId) return [];
		const cardIds = lookupTargetCardIds(cardsById, targetId);
		const cards = [...cardIds].map(id => cardsById?.[id] || { _id: id });
		return [...buildFilterFields({ cards, cardIds, getElementsForCard, keepKeys: true }), ...(tagsField ? [tagsField] : [])];
	}, [targetId, cardsById, getElementsForCard, tagsField]);

	const path = `elements[${index}].options`;
	const setFilter = useCallback(text => {
		const next = String(text || '').trim();
		onChange(`${path}.filter`, next || null);
		if (!next && strict) onChange(`${path}.strict`, false);
	}, [onChange, path, strict]);

	if (!targetId) return null;
	const targetLabel = labelOf(targetDef).toLowerCase();

	return (
		<div className='lookup-filter' data-testid='lookup-filter'>
			<span className='lookup-filter__label'>{LOOKUP_FILTER_LABEL}</span>
			<div className='lookup-filter__row'>
				{filter ? (
					<FilterChips
						query={filter}
						fields={fields}
						onChange={disabled ? undefined : setFilter}
						readOnly={disabled}
						className='lookup-filter__chips'
					/>
				) : null}
				{disabled ? null : (
					<FilterBuilder
						fields={fields}
						query={filter}
						onApply={setFilter}
						collectionId={sourceCollectionId}
						emptyText={`No conditions yet — every one of the ${targetLabel} is offered.`}
						disallowMe
						meDisallowedNote={LOOKUP_FILTER_ME_NOTE}
					/>
				)}
			</div>
			{filter ? null : (
				<Text type='secondary' className='lookup-filter__hint'>Every one of the {targetLabel} is offered. Add a condition to offer only some — retired ones left out, say.</Text>
			)}
			{filter ? (
				<ToggleRow
					label={STRICT_LABEL}
					description={driven ? STRICT_DRIVEN : (strict ? STRICT_ON : STRICT_OFF)}
					checked={strict}
					onChange={checked => onChange(`${path}.strict`, !!checked)}
					disabled={disabled || (driven && !strict)}
					name={`${path}.strict`}
					first
				/>
			) : null}
		</div>
	);
}
